import React, { useState, useEffect, useCallback } from 'react';
import { FileText, X, Upload, Trash2, Save, AlertCircle, ChevronDown, ChevronRight, Download, Eye, Play } from 'lucide-react';
import { fileRequestApi, getAccessToken } from '../lib/api';
import { VideoPlayer } from './VideoPlayer';
import type { Canvas, CanvasContent, CanvasAttachment } from '../lib/canvasTemplates';
import { PRODUCT_BRIEF_TEMPLATE } from '../lib/canvasTemplates';
import { MentionInput } from './MentionInput';

interface CanvasApiOverride {
  get: (id: string) => Promise<any>;
  upsert: (id: string, content: any, attachments?: any[]) => Promise<any>;
  uploadAttachment: (id: string, file: File) => Promise<any>;
  removeAttachment: (id: string, fileId: string) => Promise<any>;
}

interface CanvasEditorProps {
  requestId: string;
  onClose: () => void;
  onSave?: (canvas: Canvas) => void;
  /** Pass launchRequestApi.canvas to use launch-request canvas endpoints instead of file-request ones */
  canvasApiOverride?: CanvasApiOverride;
}

/** Safely extract blocks array from content that may be malformed, null, or a different shape */
function safeBlocks(content: any): any[] {
  if (!content) return [];
  if (Array.isArray(content.blocks)) return content.blocks;
  // content might be a raw array (e.g. stored as '[]' in DB)
  if (Array.isArray(content)) return [];
  return [];
}

/** Normalise whatever the API returns into a valid CanvasContent with blocks */
function normaliseContent(raw: any): CanvasContent {
  if (!raw || typeof raw !== 'object') return PRODUCT_BRIEF_TEMPLATE;
  if (Array.isArray(raw)) return PRODUCT_BRIEF_TEMPLATE;
  if (Array.isArray(raw.blocks) && raw.blocks.length > 0) return raw as CanvasContent;
  // content exists but has no blocks — use template
  return PRODUCT_BRIEF_TEMPLATE;
}

/** Groups flat blocks into sections: each heading starts a new section */
function groupBlocksIntoSections(blocks: any[]): Array<{ headingIndex: number; heading: any; contentBlocks: Array<{ block: any; originalIndex: number }> }> {
  const sections: Array<{ headingIndex: number; heading: any; contentBlocks: Array<{ block: any; originalIndex: number }> }> = [];
  let currentSection: { headingIndex: number; heading: any; contentBlocks: Array<{ block: any; originalIndex: number }> } | null = null;

  blocks.forEach((block, idx) => {
    if (block.type === 'heading') {
      if (currentSection) sections.push(currentSection);
      currentSection = { headingIndex: idx, heading: block, contentBlocks: [] };
    } else {
      if (!currentSection) {
        // blocks before the first heading — treat as a top-level section with no heading
        currentSection = { headingIndex: -1, heading: null, contentBlocks: [] };
      }
      currentSection.contentBlocks.push({ block, originalIndex: idx });
    }
  });
  if (currentSection) sections.push(currentSection);
  return sections;
}

export function CanvasEditor({ requestId, onClose, onSave, canvasApiOverride }: CanvasEditorProps) {
  const canvasApi = canvasApiOverride || fileRequestApi.canvas;
  const [content, setContent] = useState<CanvasContent>(PRODUCT_BRIEF_TEMPLATE);
  const [attachments, setAttachments] = useState<CanvasAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'unsaved'>('saved');
  const [saveTimeout, setSaveTimeout] = useState<NodeJS.Timeout | null>(null);
  // Track which sections are expanded (by heading block index); default: all expanded
  const [expandedSections, setExpandedSections] = useState<Record<number, boolean>>({});
  // Preview modal state for attachments
  const [previewAttachment, setPreviewAttachment] = useState<CanvasAttachment | null>(null);

  /** Get the best available URL for an attachment (file_url, thumbnail_url, or s3_key-based) */
  const getAttachmentUrl = (att: CanvasAttachment): string => {
    return att.file_url || att.thumbnail_url || '';
  };

  /** Get the actual video file URL (never the thumbnail — that's a poster image) */
  const getVideoSrc = (att: CanvasAttachment): string => {
    return att.file_url || '';
  };

  /** Check if attachment is a video */
  const isVideoAttachment = (att: CanvasAttachment): boolean => {
    const mime = (att as any).mime_type || att.file_type || '';
    if (mime.startsWith('video/') || mime === 'video') return true;
    const ext = att.file_name?.split('.').pop()?.toLowerCase() || '';
    return ['mp4', 'mov', 'webm', 'avi', 'mkv', 'm4v'].includes(ext);
  };

  /** Check if attachment is an image */
  const isImageAttachment = (att: CanvasAttachment): boolean => {
    const mime = (att as any).mime_type || att.file_type || '';
    if (mime.startsWith('image/') || mime === 'image') return true;
    const ext = att.file_name?.split('.').pop()?.toLowerCase() || '';
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext);
  };

  // Load existing canvas
  useEffect(() => {
    loadCanvas();
  }, [requestId]);

  const loadCanvas = async () => {
    try {
      setLoading(true);
      const response = await canvasApi.get(requestId);
      // Handle both response shapes: { data: { canvas } } and { canvas }
      const canvasData = response.data?.canvas ?? response.canvas ?? null;

      if (canvasData && canvasData.content) {
        // Parse content if it's a JSON string (some DB drivers return strings)
        let rawContent = canvasData.content;
        if (typeof rawContent === 'string') {
          try { rawContent = JSON.parse(rawContent); } catch { rawContent = null; }
        }
        const normContent = normaliseContent(rawContent);
        setContent(normContent);

        let rawAttachments = canvasData.attachments || [];
        if (typeof rawAttachments === 'string') {
          try { rawAttachments = JSON.parse(rawAttachments); } catch { rawAttachments = []; }
        }
        setAttachments(Array.isArray(rawAttachments) ? rawAttachments : []);

        // Default all sections to expanded
        const blocks = safeBlocks(normContent);
        const initial: Record<number, boolean> = {};
        blocks.forEach((b: any, i: number) => { if (b.type === 'heading') initial[i] = true; });
        setExpandedSections(initial);
      } else {
        // No canvas yet — use default template, expand all headings
        setContent(PRODUCT_BRIEF_TEMPLATE);
        const initial: Record<number, boolean> = {};
        PRODUCT_BRIEF_TEMPLATE.blocks.forEach((b, i) => { if (b.type === 'heading') initial[i] = true; });
        setExpandedSections(initial);
      }
    } catch (err: any) {
      console.error('Failed to load canvas:', err);
      setError('Failed to load canvas');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (headingIndex: number) => {
    setExpandedSections(prev => ({ ...prev, [headingIndex]: !prev[headingIndex] }));
  };

  const collapseAll = () => {
    const next: Record<number, boolean> = {};
    safeBlocks(content).forEach((b, i) => { if (b.type === 'heading') next[i] = false; });
    setExpandedSections(next);
  };

  const expandAll = () => {
    const next: Record<number, boolean> = {};
    safeBlocks(content).forEach((b, i) => { if (b.type === 'heading') next[i] = true; });
    setExpandedSections(next);
  };

  // Auto-save with debounce
  const debouncedSave = useCallback((newContent: CanvasContent) => {
    setSaveStatus('unsaved');

    // Clear existing timeout
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }

    // Set new timeout
    const timeout = setTimeout(async () => {
      await saveCanvas(newContent);
    }, 2000); // 2 second debounce

    setSaveTimeout(timeout);
  }, [saveTimeout]);

  const saveCanvas = async (contentToSave: CanvasContent) => {
    try {
      setSaveStatus('saving');
      setSaving(true);

      const response = await canvasApi.upsert(requestId, contentToSave);

      setSaveStatus('saved');
      if (onSave) {
        onSave(response.data.canvas);
      }

      // Reset saved status after 2 seconds
      setTimeout(() => {
        setSaveStatus('saved');
      }, 2000);
    } catch (err: any) {
      console.error('Failed to save canvas:', err);
      setError('Failed to save canvas');
      setSaveStatus('unsaved');
    } finally {
      setSaving(false);
    }
  };

  // Update block content
  const updateBlock = (index: number, field: string, value: any) => {
    const blocks = safeBlocks(content);
    const newBlocks = [...blocks];
    newBlocks[index] = { ...newBlocks[index], [field]: value };
    const newContent = { ...content, blocks: newBlocks };
    setContent(newContent);
    debouncedSave(newContent);
  };

  // Update list item
  const updateListItem = (blockIndex: number, itemIndex: number, value: string) => {
    const blocks = safeBlocks(content);
    const newBlocks = [...blocks];
    const items = [...(newBlocks[blockIndex].items as string[])];
    items[itemIndex] = value;
    newBlocks[blockIndex] = { ...newBlocks[blockIndex], items };
    const newContent = { ...content, blocks: newBlocks };
    setContent(newContent);
    debouncedSave(newContent);
  };

  // Update checklist item
  const updateChecklistItem = (blockIndex: number, itemIndex: number, field: string, value: any) => {
    const blocks = safeBlocks(content);
    const newBlocks = [...blocks];
    const items = [...(newBlocks[blockIndex].items as any[])];
    items[itemIndex] = { ...items[itemIndex], [field]: value };
    newBlocks[blockIndex] = { ...newBlocks[blockIndex], items };
    const newContent = { ...content, blocks: newBlocks };
    setContent(newContent);
    debouncedSave(newContent);
  };

  // Handle file upload
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      setError(null);

      const response = await canvasApi.uploadAttachment(requestId, file);
      const newAttachment = response.data.attachment;

      setAttachments([...attachments, newAttachment]);
      setSaveStatus('saved');
    } catch (err: any) {
      console.error('Failed to upload attachment:', err);
      setError('Failed to upload attachment');
    } finally {
      setUploading(false);
    }
  };

  // Handle remove attachment
  const handleRemoveAttachment = async (fileId: string) => {
    try {
      await canvasApi.removeAttachment(requestId, fileId);
      setAttachments(attachments.filter(a => a.file_id !== fileId));
    } catch (err: any) {
      console.error('Failed to remove attachment:', err);
      setError('Failed to remove attachment');
    }
  };

  // Render individual block editor (no heading — headings are rendered as section toggles)
  const renderContentBlock = (block: any, originalIndex: number) => {
    switch (block.type) {
      case 'text':
        return (
          <MentionInput
            key={originalIndex}
            value={block.content}
            onChange={(value) => updateBlock(originalIndex, 'content', value)}
            multiline={true}
            rows={4}
            className="w-full bg-transparent border border-border rounded-md outline-none text-sm text-foreground mb-3 focus:ring-2 focus:ring-ring px-3 py-2 resize-y min-h-[80px]"
          />
        );

      case 'list':
        return (
          <div key={originalIndex} className="space-y-1.5 mb-4">
            {block.items?.map((item: string, i: number) => (
              <div key={i} className="flex items-start gap-2">
                <span className="text-sm mt-1.5 text-muted-foreground shrink-0">•</span>
                <MentionInput
                  value={item}
                  onChange={(value) => updateListItem(originalIndex, i, value)}
                  className="flex-1 bg-background border border-border rounded-md outline-none text-sm focus:ring-2 focus:ring-ring px-3 py-2"
                />
              </div>
            ))}
          </div>
        );

      case 'checklist':
        return (
          <div key={originalIndex} className="space-y-2 mb-4">
            {block.items?.map((item: any, i: number) => (
              <label key={i} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={item.checked}
                  onChange={(e) => updateChecklistItem(originalIndex, i, 'checked', e.target.checked)}
                  className="rounded border-border w-4 h-4 shrink-0"
                />
                <MentionInput
                  value={item.text}
                  onChange={(value) => updateChecklistItem(originalIndex, i, 'text', value)}
                  className={`flex-1 bg-background border border-border rounded-md outline-none text-sm focus:ring-2 focus:ring-ring px-3 py-2 ${
                    item.checked ? 'line-through text-muted-foreground' : ''
                  }`}
                />
              </label>
            ))}
          </div>
        );

      case 'attachments':
        return (
          <div key={originalIndex} className="mb-4">
            {/* File upload zone */}
            <label className="border-2 border-dashed border-border rounded-md p-6 text-center block cursor-pointer hover:bg-accent transition-colors">
              <input
                type="file"
                onChange={handleFileUpload}
                className="hidden"
                disabled={uploading}
              />
              <Upload className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Images, videos, or documents up to 50MB
              </p>
            </label>

            {/* Attachments list */}
            {attachments.length > 0 && (
              <div className="space-y-2 mt-4">
                {attachments.map((attachment) => {
                  const isVideo = isVideoAttachment(attachment);
                  const isImage = isImageAttachment(attachment);
                  const url = getAttachmentUrl(attachment);

                  return (
                    <div
                      key={attachment.file_id}
                      className="flex items-center justify-between p-3 bg-muted rounded-md"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        {/* Thumbnail with video play overlay */}
                        {isVideo && attachment.thumbnail_url ? (
                          <div
                            className="relative w-12 h-12 shrink-0 cursor-pointer"
                            onClick={() => attachment.file_url && setPreviewAttachment(attachment)}
                          >
                            <img
                              src={attachment.thumbnail_url}
                              alt={attachment.file_name}
                              className="w-12 h-12 object-cover rounded"
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded">
                              <Play className="w-5 h-5 text-white" />
                            </div>
                          </div>
                        ) : isImage && attachment.thumbnail_url ? (
                          <img
                            src={attachment.thumbnail_url}
                            alt={attachment.file_name}
                            className="w-12 h-12 object-cover rounded shrink-0 cursor-pointer"
                            onClick={() => url && setPreviewAttachment(attachment)}
                          />
                        ) : attachment.thumbnail_url ? (
                          <img
                            src={attachment.thumbnail_url}
                            alt={attachment.file_name}
                            className="w-12 h-12 object-cover rounded shrink-0"
                          />
                        ) : (
                          <FileText className="w-12 h-12 text-muted-foreground shrink-0" />
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{attachment.file_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatFileSize(attachment.file_size)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {/* Preview button for images/videos */}
                        {((isImage && url) || (isVideo && attachment.file_url)) && (
                          <button
                            onClick={() => setPreviewAttachment(attachment)}
                            className="p-2 hover:bg-primary/10 text-primary rounded-md transition-colors"
                            title="Preview"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        {/* Download — use file_url, fall back to thumbnail_url */}
                        {url && (
                          <button
                            onClick={async () => {
                              let blobUrl: string | null = null;
                              try {
                                const token = getAccessToken();
                                const response = await fetch(url, {
                                  headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                                });
                                const blob = await response.blob();
                                blobUrl = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = blobUrl;
                                a.download = attachment.file_name || 'download';
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                window.URL.revokeObjectURL(blobUrl);
                              } catch {
                                if (blobUrl) window.URL.revokeObjectURL(blobUrl);
                                window.open(url, '_blank');
                              }
                            }}
                            className="p-2 hover:bg-primary/10 text-primary rounded-md transition-colors"
                            title="Download"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleRemoveAttachment(attachment.file_id)}
                          className="p-2 hover:bg-destructive/10 text-destructive rounded-md transition-colors"
                          title="Remove"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-card rounded-lg p-8">
          <p className="text-sm">Loading canvas...</p>
        </div>
      </div>
    );
  }

  const blocks = safeBlocks(content);
  const sections = groupBlocksIntoSections(blocks);
  const allExpanded = blocks.every((b, i) => b.type !== 'heading' || expandedSections[i]);
  const allCollapsed = blocks.every((b, i) => b.type !== 'heading' || !expandedSections[i]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border shrink-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Canvas Brief
            </h2>
            {/* Save status indicator */}
            <span className={`text-xs px-2 py-1 rounded ${
              saveStatus === 'saved'
                ? 'bg-green-100 text-green-700'
                : saveStatus === 'saving'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-yellow-100 text-yellow-700'
            }`}>
              {saveStatus === 'saved'
                ? '✓ Saved'
                : saveStatus === 'saving'
                ? 'Saving...'
                : 'Unsaved changes'}
            </span>
            {/* Expand / Collapse all */}
            <div className="flex gap-1 ml-2">
              <button
                onClick={expandAll}
                disabled={allExpanded}
                className="text-xs px-2 py-1 rounded border border-border hover:bg-accent disabled:opacity-40 transition-colors"
              >
                Expand All
              </button>
              <button
                onClick={collapseAll}
                disabled={allCollapsed}
                className="text-xs px-2 py-1 rounded border border-border hover:bg-accent disabled:opacity-40 transition-colors"
              >
                Collapse All
              </button>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-accent rounded-md transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="mx-4 mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md flex items-center gap-2 shrink-0">
            <AlertCircle className="w-4 h-4 text-destructive" />
            <p className="text-sm text-destructive">{error}</p>
            <button
              onClick={() => setError(null)}
              className="ml-auto p-1 hover:bg-destructive/20 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Content — accordion sections */}
        <div className="flex-1 overflow-y-auto">
          <p className="text-xs text-muted-foreground px-6 pt-4 pb-2">
            Auto-saves every 2 seconds. Click a section heading to expand/collapse it.
          </p>
          <div className="px-6 pb-6 divide-y divide-border">
            {sections.map((section, sIdx) => {
              const isExpanded = section.headingIndex === -1 || expandedSections[section.headingIndex];

              if (section.headingIndex === -1) {
                // Orphan blocks before first heading
                return (
                  <div key={`orphan-${sIdx}`} className="py-3">
                    {section.contentBlocks.map(({ block, originalIndex }) =>
                      renderContentBlock(block, originalIndex)
                    )}
                  </div>
                );
              }

              return (
                <div key={`section-${section.headingIndex}`} className="py-1">
                  {/* Section toggle header */}
                  <div
                    className="flex items-center gap-2 w-full text-left py-3 group cursor-pointer"
                    onClick={() => toggleSection(section.headingIndex)}
                  >
                    <span className="shrink-0 text-muted-foreground group-hover:text-foreground transition-colors">
                      {isExpanded
                        ? <ChevronDown className="w-4 h-4" />
                        : <ChevronRight className="w-4 h-4" />
                      }
                    </span>
                    {/* Clicking the heading text edits it; stop propagation so toggle doesn't fire */}
                    <div className="flex-1" onClick={(e) => e.stopPropagation()}>
                      <MentionInput
                        value={section.heading.content}
                        onChange={(value) => updateBlock(section.headingIndex, 'content', value)}
                        className={`w-full bg-transparent border-none outline-none font-semibold ${
                          section.heading.level === 2 ? 'text-base' : 'text-sm'
                        } focus:ring-2 focus:ring-ring rounded px-1 py-0`}
                      />
                    </div>
                    {/* Preview snippet when collapsed */}
                    {!isExpanded && section.contentBlocks.length > 0 && (
                      <span className="text-xs text-muted-foreground truncate max-w-[200px] shrink-0">
                        {(() => {
                          const firstContent = section.contentBlocks.find(cb => cb.block.content || cb.block.items);
                          if (!firstContent) return `${section.contentBlocks.length} item(s)`;
                          if (firstContent.block.content) return firstContent.block.content.slice(0, 60);
                          if (Array.isArray(firstContent.block.items)) {
                            const first = firstContent.block.items[0];
                            return typeof first === 'string' ? first.slice(0, 60) : (first as any).text?.slice(0, 60);
                          }
                          return `${section.contentBlocks.length} item(s)`;
                        })()}
                      </span>
                    )}
                  </div>

                  {/* Section content (expanded) */}
                  {isExpanded && (
                    <div className="pl-6 pt-1 pb-2">
                      {section.contentBlocks.length === 0 ? (
                        <p className="text-xs text-muted-foreground italic">No content in this section.</p>
                      ) : (
                        section.contentBlocks.map(({ block, originalIndex }) =>
                          renderContentBlock(block, originalIndex)
                        )
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border flex justify-between shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-border rounded-md hover:bg-accent transition-colors"
          >
            Close
          </button>
          <button
            onClick={() => saveCanvas(content)}
            disabled={saving || saveStatus === 'saved'}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:brightness-95 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Now'}
          </button>
        </div>
      </div>

      {/* Fullscreen Preview Modal for attachments */}
      {previewAttachment && (() => {
        const isVideo = isVideoAttachment(previewAttachment);
        const isImage = isImageAttachment(previewAttachment);
        // For videos: must use actual file_url, never thumbnail (which is a poster image)
        // For images: thumbnail or file_url both work
        const videoSrc = getVideoSrc(previewAttachment);
        const imageSrc = getAttachmentUrl(previewAttachment);
        if (isVideo && !videoSrc) return null; // No playable video source
        if (isImage && !imageSrc) return null;

        return (
          <div
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-[100]"
            onClick={() => setPreviewAttachment(null)}
          >
            <div
              className="relative w-full h-full flex items-center justify-center p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setPreviewAttachment(null)}
                className="absolute top-4 right-4 text-white hover:text-gray-300 z-20 bg-black/50 rounded-full p-2"
              >
                <X className="w-6 h-6" />
              </button>

              {isVideo ? (
                <div className="w-full h-full max-w-[90vw] max-h-[80vh] flex items-center justify-center">
                  <VideoPlayer
                    src={videoSrc}
                    poster={previewAttachment.thumbnail_url}
                    autoPlay={true}
                    className="w-full h-full"
                  />
                </div>
              ) : isImage ? (
                <img
                  src={imageSrc}
                  alt={previewAttachment.file_name}
                  className="max-w-full max-h-full rounded-lg object-contain shadow-2xl"
                  style={{ maxHeight: '90vh', maxWidth: '90vw' }}
                />
              ) : null}

              <div className="absolute bottom-4 left-4 right-4 bg-black/70 text-white p-4 rounded-lg max-w-2xl mx-auto">
                <p className="font-medium truncate">{previewAttachment.file_name}</p>
                <p className="text-xs text-gray-300 mt-1">{formatFileSize(previewAttachment.file_size)}</p>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}
